if (!customElements.get('t4-athlete-gallery')) {
  // Athlete gallery ("Trusted by champions") interaction rules:
  // - Desktop (hover device): videos play ONLY while hovered, paused on leave.
  //   Clicking the sound button toggles audio inline; it starts muted (autoplay policy).
  // - Any device: clicking the video opens the site-wide video popup
  //   (same design classes as sections/video-popup.liquid, portrait variant).
  //   The SAME video element moves into the popup, so playback continues from
  //   where it was - no restart, no duplicate instance. On close it moves back
  //   into its gallery card.
  customElements.define(
    't4-athlete-gallery',
    class T4AthleteGallery extends HTMLElement {
      connectedCallback() {
        this.videos = Array.from(this.querySelectorAll('[data-athlete-video]'));
        if (!this.videos.length) return;

        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.onClick = this.onClick.bind(this);
        this.addEventListener('click', this.onClick);

        if (this.canHover && !this.reduceMotion) {
          // Autoplay on hover only - never on visibility, never with sound.
          this.videos.forEach((video) => {
            const wrap = video.closest('[data-athlete-media]');
            if (!wrap) return;
            wrap.addEventListener('mouseenter', () => {
              if (this.popup) return;
              this.load(video);
              if (!video.dataset.userPaused) this.play(video);
            });
            wrap.addEventListener('mouseleave', () => {
              if (this.popup) return;
              video.pause();
              this.sync(video);
            });
          });
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
      }

      load(video) {
        if (video.dataset.loaded) return;
        video.querySelectorAll('source[data-src]').forEach((source) => {
          source.src = source.dataset.src;
        });
        video.load();
        video.dataset.loaded = 'true';
      }

      play(video) {
        video.play().then(() => this.sync(video)).catch(() => this.sync(video));
      }

      toggleSound(video) {
        video.muted = !video.muted;
        this.sync(video);
        if (video.paused) this.play(video);
      }

      soundButton(video) {
        return video.parentElement.querySelector('[data-athlete-sound]');
      }

      sync(video) {
        const button = this.soundButton(video);
        if (button) {
          button.setAttribute('aria-pressed', String(video.muted === false));
          button.setAttribute('aria-label', video.muted === false ? 'Turn sound off' : 'Turn sound on');
          button.classList.toggle('is-sounding', video.muted === false);
        }
      }

      onClick(event) {
        const sound = event.target.closest('[data-athlete-sound]');
        if (sound) {
          const media = sound.closest('[data-athlete-media]');
          const video = media && media.querySelector('[data-athlete-video]');
          if (!video) return;
          this.load(video);
          this.toggleSound(video);
          return;
        }
        const media = event.target.closest('[data-athlete-media]');
        if (!media) return;
        const video = media.querySelector('[data-athlete-video]');
        if (!video) return;
        // Any device: clicking the video opens the popup; the same element
        // moves in and continues - no restart, no duplicate.
        this.openPopup(media, video);
      }

      openPopup(media, sourceVideo) {
        if (this.popup) return;
        this.load(sourceVideo);

        const popup = document.createElement('div');
        popup.className = 't4-video-popup t4-video-popup--athlete is-open';
        popup.setAttribute('role', 'dialog');
        popup.setAttribute('aria-modal', 'true');
        popup.setAttribute('aria-label', 'Athlete video');

        // Copy the merchant's live popup design variables from the site-wide popup,
        // so the design matches whatever settings are saved in the theme editor.
        let vars = 'position:fixed;inset:0;z-index:2147483000;';
        const sitePopup = document.querySelector('[data-video-popup]');
        if (sitePopup) {
          const cs = getComputedStyle(sitePopup);
          ['--t4-popup-overlay', '--t4-popup-overlay-color', '--t4-popup-button', '--t4-popup-button-text', '--t4-popup-card', '--t4-popup-radius', '--t4-popup-blur'].forEach((name) => {
            const value = cs.getPropertyValue(name).trim();
            if (value) vars += name + ':' + value + ';';
          });
        }
        popup.style.cssText = vars;

        popup.innerHTML =
          '<div class="t4-video-popup__backdrop" data-popup-backdrop style="position:absolute;inset:0;"></div>' +
          '<div class="t4-video-popup__card">' +
          '  <div class="t4-video-popup__stage">' +
          '  <div class="t4-video-popup__media">' +
          '    <div class="t4-video-popup__frame" data-media="mobile"></div>' +
          '    <button type="button" class="t4-video-popup__mute" data-popup-mute data-muted="true" aria-label="Turn sound on">' +
          '      <svg class="t4-video-popup__icon t4-video-popup__icon--muted" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">' +
          '        <path d="M4 9h3l4-3v12l-4-3H4z" fill="currentColor"></path>' +
          '        <path d="M16 9l5 6M21 9l-5 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path>' +
          '      </svg>' +
          '      <svg class="t4-video-popup__icon t4-video-popup__icon--sound" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">' +
          '        <path d="M4 9h3l4-3v12l-4-3H4z" fill="currentColor"></path>' +
          '        <path d="M16 8.5a5 5 0 010 7M18.5 6a8.5 8.5 0 010 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"></path>' +
          '      </svg>' +
          '    </button>' +
          '  </div>' +
          '  </div>' +
          '  <button type="button" class="t4-video-popup__close" data-popup-close>' +
          '    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">' +
          '      <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path>' +
          '    </svg>' +
          '    <span class="visually-hidden">Close video</span>' +
          '  </button>' +
          '</div>';

        // Move the SAME video element into the popup: playback position and
        // sound carry over, and no second video instance is ever created.
        this.restoreParent = sourceVideo.parentElement;
        this.restoreNext = sourceVideo.nextSibling;
        sourceVideo.classList.add('t4-video-popup__video');
        popup.querySelector('[data-media="mobile"]').appendChild(sourceVideo);
        const stage = popup.querySelector('.t4-video-popup__media');

        // Size the stage to the video's native aspect once known (the CSS
        // 250/400 portrait ratio stays as the fallback for not-yet-loaded).
        const fitStage = () => {
          if (sourceVideo.videoWidth) {
            stage.style.aspectRatio = sourceVideo.videoWidth + ' / ' + sourceVideo.videoHeight;
          } else {
            sourceVideo.addEventListener('loadedmetadata', fitStage, { once: true });
          }
        };
        fitStage();

        this.popup = popup;
        document.body.appendChild(popup);
        document.body.classList.add('t4-video-popup-open');

        const video = sourceVideo;
        const muteBtn = popup.querySelector('[data-popup-mute]');
        const closeBtn = popup.querySelector('[data-popup-close]');
        // The sound may already be on (viewer tapped the sound button first);
        // the popup's mute control must reflect that same state.
        muteBtn.setAttribute('data-muted', String(video.muted));
        const restoreVideo = () => {
          video.classList.remove('t4-video-popup__video');
          if (this.restoreParent) {
            this.restoreParent.insertBefore(video, this.restoreNext);
          }
          this.restoreParent = null;
          this.restoreNext = null;
          this.sync(video);
        };
        const onClose = () => {
          video.pause();
          restoreVideo();
          popup.remove();
          this.popup = null;
          document.body.classList.remove('t4-video-popup-open');
          document.removeEventListener('keydown', this.onPopupKey);
        };
        muteBtn.addEventListener('click', () => {
          video.muted = !video.muted;
          muteBtn.setAttribute('data-muted', String(video.muted));
          this.sync(video);
          video.play().catch(() => {});
        });
        closeBtn.addEventListener('click', onClose);
        popup.querySelector('[data-popup-backdrop]').addEventListener('click', onClose);
        this.onPopupKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', this.onPopupKey);
        video.play().catch(() => {});
      }
    }
  );
}
